import { API, getToken } from "@/lib/api";
import { Loader2, Music, Clock, Lightbulb, Download, Trash2, AlertTriangle } from "lucide-react";
import { CopyButton } from "@/components/translator/shared";

const GOAL = { clientes: "Atraer clientes", conductores: "Captar conductores" };
const PLAT = { instagram: "Instagram", whatsapp: "WhatsApp Estados" };
const STEP = { text: "Escribiendo el texto…", image: "Diseñando la imagen con el logo…" };

export function CampaignCard({ c, onDelete }) {
  const imgUrl = `${API}/marketing/campaigns/${c.id}/image?auth=${encodeURIComponent(getToken())}`;
  const copy = c.copy || {};
  const tags = (copy.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ");
  const busy = c.status === "text" || c.status === "image";

  return (
    <article className="border-2 border-black bg-white grid grid-cols-1 md:grid-cols-[minmax(0,360px)_1fr]" data-testid={`campaign-${c.id}`}>
      <div className="bg-zinc-100 border-b-2 md:border-b-0 md:border-r-2 border-black flex items-center justify-center min-h-[280px]">
        {c.status === "ready" ? (
          <img src={imgUrl} alt={copy.headline} className="w-full h-full object-contain" data-testid={`campaign-image-${c.id}`} />
        ) : c.status === "error" ? (
          <div className="text-sm text-red-700 flex items-center gap-2 p-6"><AlertTriangle size={16} /> {c.error}</div>
        ) : (
          <div className="text-sm text-zinc-500 flex items-center gap-2 p-6" data-testid={`campaign-progress-${c.id}`}>
            <Loader2 className="animate-spin" size={16} /> {STEP[c.status]}
          </div>
        )}
      </div>
      <div className="p-5 space-y-4 min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{GOAL[c.goal]} · {PLAT[c.platform]} · {c.created_by_name}</div>
            <h3 className="font-display text-xl font-bold">{copy.headline || (busy ? "Generando…" : "—")}</h3>
          </div>
          <div className="flex gap-2">
            {c.status === "ready" && (
              <a href={imgUrl} download={`ds-publicidad-${c.id.slice(0, 6)}.png`} className="p-2 border border-black hover:bg-black hover:text-white transition-colors" data-testid={`campaign-download-${c.id}`} aria-label="Descargar imagen">
                <Download size={15} />
              </a>
            )}
            <button onClick={() => onDelete(c.id)} className="p-2 border border-black hover:bg-[#E63946] hover:text-white transition-colors" data-testid={`campaign-delete-${c.id}`} aria-label="Eliminar">
              <Trash2 size={15} />
            </button>
          </div>
        </div>
        {copy.caption && (
          <section>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Descripción</div>
              <CopyButton text={copy.caption} testId={`campaign-copy-caption-${c.id}`} />
            </div>
            <p className="text-sm whitespace-pre-wrap" data-testid={`campaign-caption-${c.id}`}>{copy.caption}</p>
          </section>
        )}
        {tags && (
          <section>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Hashtags</div>
              <CopyButton text={tags} testId={`campaign-copy-hashtags-${c.id}`} />
            </div>
            <p className="text-sm text-[#1d4ed8] break-words" data-testid={`campaign-hashtags-${c.id}`}>{tags}</p>
          </section>
        )}
        {copy.song && (
          <section className="bg-zinc-50 border-l-4 border-[#F5B301] p-3" data-testid={`campaign-song-${c.id}`}>
            <div className="text-sm font-semibold flex items-center gap-2"><Music size={14} /> {copy.song.title} — {copy.song.artist}</div>
            <div className="text-xs text-zinc-600 mt-1">{copy.song.why}</div>
          </section>
        )}
        {copy.best_time && <div className="text-xs text-zinc-600 flex gap-2"><Clock size={14} className="shrink-0" /> {copy.best_time}</div>}
        {(copy.tips || []).length > 0 && (
          <ul className="text-xs text-zinc-600 space-y-1">
            {copy.tips.map((t, i) => <li key={i} className="flex gap-2"><Lightbulb size={13} className="shrink-0 mt-0.5" /> {t}</li>)}
          </ul>
        )}
      </div>
    </article>
  );
}
